import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  createUser,
  deleteUser,
  getUser,
  listUsers,
  listUserMatches,
  listUserPotClaims,
  listUserInventory,
  promoteUser,
  recalculateUserPaySegment,
  resetUserIAPPurchases,
  resetUserProgress,
  setUserStatus,
  updateUser,
} from './api';
import {
  CreateUserInput,
  UpdateUserInput,
  UserRole,
  UserStatus,
} from './types';

export const adminUsersQueryKey = ['admin-users'] as const;

export function useUserInventory(id: string) {
  return useQuery({
    queryKey: ['admin-user-inventory', id],
    queryFn: () => listUserInventory(id),
    enabled: !!id,
  });
}

export function useUserDetail(id: string) {
  return useQuery({
    queryKey: [...adminUsersQueryKey, id],
    queryFn: () => getUser(id),
    enabled: !!id,
  });
}

export function useUserMatches(id: string, page: number, perPage: number) {
  return useQuery({ queryKey: ['admin-user-matches', id, page, perPage], queryFn: () => listUserMatches(id, page, perPage), placeholderData: keepPreviousData, enabled: !!id });
}

export function useUsers(
  page: number,
  search: string,
  role?: UserRole,
  status?: UserStatus
) {
  return useQuery({
    queryKey: [...adminUsersQueryKey, page, search, role, status],
    queryFn: () => listUsers({ page, search, role, status }),
    placeholderData: keepPreviousData,
  });
}

export function useCreateUserMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) => createUser(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUsersQueryKey });
    },
  });
}

export function useUpdateUserMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUserInput }) =>
      updateUser(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUsersQueryKey });
    },
  });
}

export function useDeleteUserMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUsersQueryKey });
    },
  });
}

export function usePromoteUserMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => promoteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUsersQueryKey });
    },
  });
}

export function useSetUserStatusMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: UserStatus }) =>
      setUserStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUsersQueryKey });
    },
  });
}

export function useResetUserIAPPurchasesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => resetUserIAPPurchases(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-pay-segments'] });
      queryClient.invalidateQueries({ queryKey: ['admin-transactions', 'iap'] });
      queryClient.invalidateQueries({ queryKey: ['admin-transaction', 'iap'] });
    },
  });
}

export function useRecalculateUserPaySegmentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => recalculateUserPaySegment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-pay-segments'] });
    },
  });
}

export function useResetUserProgressMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => resetUserProgress(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: adminUsersQueryKey });
      queryClient.invalidateQueries({ queryKey: ['admin-user-inventory', id] });
    },
  });
}

export function useUserPotClaims(id: string, page: number, perPage: number) {
  return useQuery({ queryKey: ['admin-user-pot-claims', id, page, perPage],
    queryFn: () => listUserPotClaims(id, page, perPage), placeholderData: keepPreviousData, enabled: !!id });
}
